// The one HTTP client of the console and admin: bearer token, the per-tab org as `Nodus-Org` (ADR-107),
// `Idempotency-Key` on every create and state change (ADR-030), dry-run and `If-Match` (ADR-031), and errors as
// ApiError from metav1.Status. The orval-generated hooks use it as their mutator (nodusMutator).
import { ApiError, NetworkError, toStatus } from "./errors";

export interface ClientConfig {
  /** `https://api.<domain>`; empty for same-origin (the MSW mock and the local dev proxy). */
  baseURL: string;
  getToken: () => Promise<string | undefined>;
  /** Called once on a 401 to refresh the session; the request is retried with the new token. */
  refreshToken?: () => Promise<string | undefined>;
  /** Called when refresh fails: the shell shows "Sign in again to continue" (ui-spec §4.18). */
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

export interface RequestOptions {
  org?: string;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  ifMatch?: string;
  contentType?: string;
  accept?: string;
  signal?: AbortSignal;
}

export interface ApiResponse<T> {
  data: T;
  headers: Headers;
  /** `Idempotent-Replayed: true`: the server returned the earlier result for this key (success, ui-spec §4.17). */
  replayed: boolean;
  requestId?: string;
  etag?: string;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export class NodusClient {
  readonly config: ClientConfig;
  /** The org of this tab's URL; requests without an explicit org use it. */
  org?: string;

  constructor(config: ClientConfig) {
    this.config = config;
  }

  url(path: string, query?: RequestOptions["query"]): string {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined) qs.set(k, String(v));
    const s = qs.toString();
    return `${this.config.baseURL}${path}${s ? `?${s}` : ""}`;
  }

  /** Headers for a request: auth, org, idempotency and preconditions. */
  async headers(opts: RequestOptions, token?: string): Promise<Headers> {
    const h = new Headers(opts.headers);
    const t = token ?? (await this.config.getToken());
    if (t) h.set("Authorization", `Bearer ${t}`);
    const org = opts.org ?? this.org;
    if (org) h.set("Nodus-Org", org);
    if (opts.idempotencyKey) h.set("Idempotency-Key", opts.idempotencyKey);
    if (opts.ifMatch) h.set("If-Match", opts.ifMatch);
    if (opts.accept) h.set("Accept", opts.accept);
    else if (!h.has("Accept")) h.set("Accept", "application/json");
    if (opts.body !== undefined && !(opts.body instanceof FormData) && !(opts.body instanceof Blob)) {
      h.set("Content-Type", opts.contentType ?? "application/json");
    }
    return h;
  }

  /** Sends a request and returns the raw Response (streams use this); throws ApiError on a non-2xx status. */
  async send(method: string, path: string, opts: RequestOptions = {}): Promise<Response> {
    const doFetch = this.config.fetch ?? fetch;
    const body =
      opts.body === undefined
        ? undefined
        : opts.body instanceof FormData || opts.body instanceof Blob || typeof opts.body === "string"
          ? (opts.body as BodyInit)
          : JSON.stringify(opts.body);
    const attempt = async (token?: string) => {
      try {
        return await doFetch(this.url(path, opts.query), {
          method,
          headers: await this.headers(opts, token),
          ...(body !== undefined ? { body } : {}),
          ...(opts.signal ? { signal: opts.signal } : {}),
        });
      } catch (e) {
        if ((e as { name?: string }).name === "AbortError") throw e;
        throw new NetworkError(e instanceof Error ? e.message : "network error", e);
      }
    };
    let res = await attempt();
    if (res.status === 401 && this.config.refreshToken) {
      const fresh = await this.config.refreshToken().catch(() => undefined);
      if (fresh) res = await attempt(fresh);
      if (res.status === 401) this.config.onUnauthorized?.();
    }
    if (!res.ok) {
      const text = await res.text();
      let parsed: unknown = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
      const retryAfter = Number(res.headers.get("Retry-After"));
      throw new ApiError(
        res.status,
        toStatus(res.status, parsed),
        res.headers.get("Nodus-Request-Id") ?? undefined,
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
      );
    }
    return res;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<ApiResponse<T>> {
    const res = await this.send(method, path, opts);
    const text = res.status === 204 ? "" : await res.text();
    const type = res.headers.get("Content-Type") ?? "";
    const data = (text && type.includes("json") ? JSON.parse(text) : text) as T;
    return {
      data,
      headers: res.headers,
      replayed: res.headers.get("Idempotent-Replayed") === "true",
      ...(res.headers.get("Nodus-Request-Id") ? { requestId: res.headers.get("Nodus-Request-Id")! } : {}),
      ...(res.headers.get("ETag") ? { etag: res.headers.get("ETag")! } : {}),
    };
  }

  get<T>(path: string, opts?: RequestOptions) {
    return this.request<T>("GET", path, opts).then((r) => r.data);
  }

  post<T>(path: string, body: unknown, opts: RequestOptions = {}) {
    return this.request<T>("POST", path, { ...opts, body });
  }

  patch<T>(path: string, body: unknown, opts: RequestOptions = {}) {
    return this.request<T>("PATCH", path, { contentType: "application/merge-patch+json", ...opts, body });
  }

  put<T>(path: string, body: unknown, opts: RequestOptions = {}) {
    return this.request<T>("PUT", path, { ...opts, body });
  }

  delete<T>(path: string, opts: RequestOptions = {}) {
    return this.request<T>("DELETE", path, opts);
  }
}

let defaultClient: NodusClient | undefined;

export function setDefaultClient(client: NodusClient) {
  defaultClient = client;
}

export function getDefaultClient(): NodusClient {
  if (!defaultClient) throw new Error("the API client is not configured yet");
  return defaultClient;
}

/** The error every generated hook rejects with: orval reads this name from the mutator's module. */
export type ErrorType<_Body> = ApiError;

/**
 * orval mutator: generated functions and hooks call this with the path and RequestInit they built. A write without
 * an `Idempotency-Key` header gets a fresh one (ADR-030), and the generated content type (merge patch) is kept.
 */
export async function nodusMutator<T>(url: string, init: RequestInit = {}): Promise<T> {
  const client = getDefaultClient();
  const method = init.method ?? "GET";
  const headers = new Headers(init.headers);
  const contentType = headers.get("Content-Type") ?? undefined;
  const idempotencyKey = headers.get("Idempotency-Key") ?? (method === "GET" ? undefined : newIdempotencyKey());
  headers.delete("Content-Type");
  headers.delete("Idempotency-Key");
  const body = typeof init.body === "string" ? JSON.parse(init.body) : init.body;
  const res = await client.request<T>(method, url, {
    headers: Object.fromEntries(headers.entries()),
    ...(contentType ? { contentType } : {}),
    ...(idempotencyKey ? { idempotencyKey } : {}),
    ...(body !== undefined && body !== null ? { body } : {}),
    ...(init.signal ? { signal: init.signal } : {}),
  });
  return res.data;
}
